import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { ToastService } from './toast';
import { ChangeLangService } from './change-lang-service';
import { SEND_SESSION_TOKEN, SILENT_REQUEST } from './http-context';
import { SessionService } from './session';
import { isInvalidSessionToken, SessionExpiryService } from './session-expiry';
import { environment } from '../../environment.dev';

export const httpInterceptor: HttpInterceptorFn = (req, next) => {
  const toastService = inject(ToastService);
  const langService = inject(ChangeLangService);
  const sessionService = inject(SessionService);
  const sessionExpiry = inject(SessionExpiryService);

  const setHeaders: Record<string, string> = {
    'X-Parse-REST-API-Key': environment.clientKey,
    'X-Parse-Application-Id': environment.appId,
  };

  const sessionToken = sessionService.getSessionToken();
  const isAuthEndpoint =
    !req.context.get(SEND_SESSION_TOKEN) &&
    (req.url.includes('/functions/login') ||
      req.url.includes('/functions/loginAnonymous') ||
      req.url.includes('/functions/loginWithGoogle') ||
      req.url.includes('/functions/loginWithApple') ||
      req.url.includes('/functions/loginWithUaePass') ||
      req.url.includes('/functions/userSignUp') ||
      req.url.includes('/functions/generateOTP') ||
      req.url.includes('/functions/verifyOTP'));

  const carriesSession = !!sessionToken && !isAuthEndpoint;

  if (carriesSession) {
    setHeaders['X-Parse-Session-Token'] = sessionToken!;
  }

  const clonedRequest = req.clone({ setHeaders });

  const report = (error: HttpErrorResponse) => {
    if (!req.context.get(SILENT_REQUEST)) {
      toastService.showToast('error', getErrorMessage(error, langService), '');
    }
    return throwError(() => error);
  };

  /** Re-issues the request with a token that replaced the one it was sent with. */
  const replay = (token: string) =>
    next(
      clonedRequest.clone({ setHeaders: { 'X-Parse-Session-Token': token } }),
    ).pipe(catchError(report));

  return next(clonedRequest).pipe(
    catchError((error: HttpErrorResponse) => {
      if (!carriesSession || !isInvalidSessionToken(error)) {
        return report(error);
      }

      // The stored token may already have moved on: a sibling request that
      // failed a moment earlier could have renewed it, or signed the user out.
      const currentToken = sessionService.getSessionToken();
      if (!currentToken) {
        // Sign-out is under way and has said its piece — stay quiet.
        return throwError(() => error);
      }
      if (currentToken !== sessionToken) {
        return replay(currentToken);
      }

      if (sessionExpiry.recoveryFor() === 'sign-out') {
        // expireSession() explains itself and redirects; a second, generic
        // "invalid session token" toast would only add noise.
        sessionExpiry.expireSession();
        return throwError(() => error);
      }

      // A guest's anonymous token went stale — mint a new one and replay.
      return sessionExpiry.renewGuestSession().pipe(
        switchMap((token) => (token ? replay(token) : report(error))),
      );
    })
  );
};

/**
 * Reads a displayable message out of a failed response.
 *
 * Exported so components show the same text as the toast. Only a real string is
 * ever returned — an unexpected payload shape used to reach the template as
 * `[object Object]`.
 */
export function getErrorMessage(
  error: unknown,
  langService: ChangeLangService,
  fallback = 'An unexpected error occurred.'
): string {
  const body = (error as { error?: any })?.error;
  const errorData = body?.error;

  if (errorData && typeof errorData === 'object') {
    const currentLang = langService.currentLang() || 'en';
    const localized =
      errorData[currentLang] ?? errorData.en ?? errorData.ar ?? errorData.message;
    if (typeof localized === 'string' && localized.trim()) {
      return localized;
    }
  }

  if (typeof errorData === 'string' && errorData.trim()) {
    return errorData;
  }

  if (typeof body === 'string' && body.trim()) {
    return body;
  }

  return fallback;
}
